/**
 * Partner campaign tracking — route /partner/campaigns/:id.
 *
 * The corporate's own view of one campaign: status, the shareable tracking link
 * + QR they distribute, and the live funnel (scans → entries → opt-in leads).
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PartnerShell } from '../components/PartnerShell';
import { ApiError, type CampaignRead } from '../lib/api';
import {
  downloadMyCampaignLeadsCsv,
  downloadObjectUrl,
  fetchMyCampaignFlyerUrl,
  getMyCampaign,
  getMyCampaignFlyers,
  getMyCampaignFunnel,
  getMyCampaignLeads,
  type CampaignFunnel,
  type PartnerFlyerItem,
  type PartnerLeadItem,
} from '../lib/partnerApi';
import { isPartnerAuthed } from '../lib/partnerAuth';

function Kpi({
  label,
  value,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
}) {
  return (
    <div className="rounded-2xl border border-line bg-white p-5 shadow-card">
      <p className="text-2xl font-bold text-ink">{value}</p>
      <p className="mt-1 text-xs text-ink-500">{label}</p>
      {sub && <p className="mt-0.5 text-[11px] text-ink-400">{sub}</p>}
    </div>
  );
}

/** Whole-number percentage of `part` out of `whole`, or null when undefined. */
const pct = (part: number, whole: number): number | null =>
  whole > 0 ? Math.round((part / whole) * 100) : null;

function CopyRow({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — link still selectable */
    }
  }
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line py-2 last:border-0">
      <span className="shrink-0 text-sm text-ink-500">{label}</span>
      <span className="flex min-w-0 items-center gap-2">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="truncate text-sm text-primary hover:underline"
          title={url}
        >
          {url}
        </a>
        <button
          type="button"
          onClick={copy}
          className="shrink-0 rounded-md border border-line px-2 py-0.5 text-xs font-medium text-ink-700 hover:bg-surface-muted"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </span>
    </div>
  );
}

const num = (v: unknown): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : 0;

/** Inline download icon (SVG, not emoji) for the CSV button. */
function DownloadIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3v12m0 0 4-4m-4 4-4-4" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  );
}

/**
 * "Your leads" — the partner's OWN consented, contactable leads for this
 * campaign. Lists name / email / phone (decrypted server-side, consent-gated)
 * with a POPIA note and a CSV download. Self-fetching + best-effort: a failure
 * here never blocks the rest of the page.
 */
function YourLeads({ campaignId }: { campaignId: string }) {
  const [leads, setLeads] = useState<PartnerLeadItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLeads(null);
    setLoadError(null);
    getMyCampaignLeads(campaignId)
      .then((res) => live && setLeads(res.leads))
      .catch((err) => {
        if (!live || (err instanceof ApiError && err.status === 401)) return;
        setLoadError(
          err instanceof ApiError ? err.message : 'Could not load your leads.',
        );
      });
    return () => {
      live = false;
    };
  }, [campaignId]);

  async function download() {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadMyCampaignLeadsCsv(campaignId);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return; // bounced to login
      setDownloadError(
        err instanceof ApiError ? err.message : 'Could not download the CSV.',
      );
    } finally {
      setDownloading(false);
    }
  }

  const hasLeads = (leads?.length ?? 0) > 0;

  return (
    <section
      aria-labelledby="your-leads-heading"
      className="rounded-2xl border border-line bg-white p-6 shadow-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="your-leads-heading" className="text-base font-semibold text-ink">
            Your leads
          </h2>
          <p className="mt-1 max-w-prose text-xs text-ink-500">
            These entrants consented to be contacted by you. Use their details in
            line with POPIA — only for what they opted in to.
          </p>
        </div>
        <button
          type="button"
          onClick={download}
          disabled={downloading || !hasLeads}
          aria-busy={downloading}
          className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-line px-3 py-1.5 text-sm font-medium text-ink-700 transition-colors duration-200 hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <DownloadIcon />
          {downloading ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>

      {downloadError && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {downloadError}
        </p>
      )}

      <div className="mt-4">
        {loadError && (
          <p role="alert" className="text-sm text-red-600">
            {loadError}
          </p>
        )}

        {!loadError && leads === null && (
          <p className="text-sm text-ink-500">Loading your leads…</p>
        )}

        {!loadError && leads !== null && !hasLeads && (
          <div className="rounded-xl border border-dashed border-line bg-surface-muted/40 px-4 py-8 text-center">
            <p className="text-sm font-medium text-ink">No consented leads yet</p>
            <p className="mt-1 text-xs text-ink-500">
              When entrants opt in to be contacted, their details appear here for
              you to download.
            </p>
          </div>
        )}

        {!loadError && hasLeads && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[36rem] border-collapse text-left text-sm">
              <caption className="sr-only">
                Consented contactable leads for this campaign
              </caption>
              <thead>
                <tr className="border-b border-line text-xs uppercase tracking-wide text-ink-400">
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Name
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Email
                  </th>
                  <th scope="col" className="py-2 font-medium">
                    Phone
                  </th>
                </tr>
              </thead>
              <tbody>
                {leads!.map((lead) => (
                  <tr
                    key={lead.lead_id}
                    className="border-b border-line last:border-0"
                  >
                    <td className="py-2.5 pr-4 text-ink">{lead.name || '—'}</td>
                    <td className="py-2.5 pr-4 text-ink-700">
                      {lead.email ? (
                        <a
                          href={`mailto:${lead.email}`}
                          className="text-primary hover:underline"
                        >
                          {lead.email}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2.5 text-ink-700">{lead.phone || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-ink-400">
              {leads!.length} contactable {leads!.length === 1 ? 'lead' : 'leads'}.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

/** One branded flyer tile (preview + download). The flyer endpoint is auth-gated,
 *  so the JPEG is fetched as a blob (object URL) for both preview and download. */
function FlyerTile({ flyer, campaignId, index }: { flyer: PartnerFlyerItem; campaignId: string; index: number }) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    let createdUrl: string | null = null;
    fetchMyCampaignFlyerUrl(flyer.download_path)
      .then((url) => {
        if (!live) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setPreviewUrl(url);
      })
      .catch(() => {});
    return () => {
      live = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [flyer.download_path]);

  if (!previewUrl) return null;
  const filename = index === 0 ? `flyer-${campaignId}.jpg` : `flyer-${campaignId}-${index + 1}.jpg`;

  return (
    <div className="flex w-40 flex-col gap-2">
      <img
        src={previewUrl}
        alt="Branded competition flyer with QR code"
        className="w-40 rounded-xl border border-line bg-white shadow-card"
      />
      <button
        type="button"
        onClick={() => downloadObjectUrl(previewUrl, filename)}
        className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
      >
        <DownloadIcon />
        Download
      </button>
    </div>
  );
}

/**
 * Branded, shareable flyers for the campaign — prizes' APPROVED competition flyers
 * (hero prize + entry details, AI-generated OR marketing-uploaded) with THIS
 * campaign's tracking QR. Only approved flyers surface (enforced server-side).
 * Self-fetching + best-effort: with no approved flyer (or prizes unreachable) it
 * renders nothing, so the caller's plain-QR fallback shows instead.
 */
function CampaignFlyer({
  campaignId,
  onLoaded,
}: {
  campaignId: string;
  onLoaded: (hasFlyer: boolean) => void;
}) {
  const [flyers, setFlyers] = useState<PartnerFlyerItem[]>([]);

  useEffect(() => {
    let live = true;
    getMyCampaignFlyers(campaignId)
      .then((res) => {
        if (!live) return;
        setFlyers(res.flyers);
        onLoaded(res.flyers.length > 0);
      })
      .catch(() => live && onLoaded(false));
    return () => {
      live = false;
    };
  }, [campaignId, onLoaded]);

  if (flyers.length === 0) return null;

  return (
    <div className="mt-4">
      <p className="text-sm font-medium text-ink">
        Ready-to-share {flyers.length === 1 ? 'flyer' : 'flyers'}
      </p>
      <p className="mt-1 max-w-prose text-xs text-ink-500">
        The prize, the entry details and your tracking QR — download to print or post on
        your channels. Every scan is attributed back to this campaign.
      </p>
      <div className="mt-3 flex flex-wrap gap-5">
        {flyers.map((f, i) => (
          <FlyerTile key={f.flyer_id} flyer={f} campaignId={campaignId} index={i} />
        ))}
      </div>
    </div>
  );
}

export function PartnerCampaignTrackPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<CampaignRead | null>(null);
  const [funnel, setFunnel] = useState<CampaignFunnel | null>(null);
  const [error, setError] = useState<string | null>(null);
  // True once a branded flyer is available — demotes the plain QR to a fallback.
  const [hasFlyer, setHasFlyer] = useState(false);

  useEffect(() => {
    if (!isPartnerAuthed()) {
      navigate('/partner/login', { replace: true });
      return;
    }
    if (!id) return;
    let live = true;
    getMyCampaign(id)
      .then((c) => live && setCampaign(c))
      .catch((err) => {
        if (!live || (err instanceof ApiError && err.status === 401)) return;
        setError(err instanceof ApiError ? err.message : 'Could not load this campaign.');
      });
    // Funnel is best-effort — a failure here must not block the page.
    getMyCampaignFunnel(id)
      .then((f) => live && setFunnel(f))
      .catch(() => live && setFunnel(null));
    return () => {
      live = false;
    };
  }, [id, navigate]);

  const f = funnel?.funnel ?? {};
  const scans = num(f['qr_scan']) + num(f['link_click']);
  const entries = num(f['skill_completed']) || num(f['registration']);
  const leads = num(funnel?.leads) || num(f['lead_opt_in']);
  // Entries shown as the entry rate — what share of the traffic actually entered.
  const entryRate = pct(entries, scans);
  const conv =
    typeof funnel?.conversion_rate === 'number'
      ? `${Math.round(funnel.conversion_rate * 100)}%`
      : '—';

  return (
    <PartnerShell title={campaign?.name ?? 'Campaign'}>
      <Link to="/partner" className="mb-4 inline-block text-sm text-primary hover:underline">
        ← All campaigns
      </Link>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!error && !campaign && <p className="text-sm text-ink-500">Loading…</p>}
      {campaign && (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-ink-700">
              {campaign.status}
            </span>
            <span className="text-sm text-ink-500">{campaign.prize_title}</span>
          </div>

          {/* Live tracking */}
          <section>
            <h2 className="mb-3 text-base font-semibold text-ink">Tracking</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Kpi label="QR scans + clicks" value={scans} />
              <Kpi
                label="Entry rate"
                value={entryRate === null ? '—' : `${entryRate}%`}
              />
              <Kpi label="Opt-in leads" value={leads} />
              <Kpi label="Conversion" value={conv} />
            </div>
            {!funnel && (
              <p className="mt-2 text-xs text-ink-500">
                Live numbers appear once the competition is live and entries start.
              </p>
            )}
          </section>

          {/* Distribution assets */}
          <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
            <h2 className="mb-3 text-base font-semibold text-ink">Your links &amp; QR</h2>
            <p className="mb-3 text-xs text-ink-500">
              Put these on your product, in-store and on your channels — every scan
              and click is attributed back to this campaign.
            </p>
            <dl>
              {campaign.public_url && <CopyRow label="Competition page" url={campaign.public_url} />}
              {campaign.tracking_url && <CopyRow label="Tracking link" url={campaign.tracking_url} />}
            </dl>

            {/* Branded flyer (primary) — renders only when an approved flyer exists. */}
            {id && <CampaignFlyer campaignId={id} onLoaded={setHasFlyer} />}

            {/* Plain QR — ONLY when there's no branded flyer (fallback). When a flyer
                exists it already carries the tracking QR, so we don't show a second one. */}
            {campaign.qr_png_url && !hasFlyer && (
              <div className="mt-4 flex items-center gap-4">
                <img
                  src={campaign.qr_png_url}
                  alt="Campaign QR code"
                  className="h-32 w-32 rounded-lg border border-line bg-white p-2"
                />
                <a
                  href={campaign.qr_png_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="rounded-lg border border-line px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-surface-muted"
                >
                  Download QR
                </a>
              </div>
            )}
          </section>

          {/* Your leads — consented, contactable entrants (POPIA-gated) */}
          {id && <YourLeads campaignId={id} />}
        </div>
      )}
    </PartnerShell>
  );
}

export default PartnerCampaignTrackPage;
